// sort-imports-ignore
import './services/sentry'

import isPropValid from '@emotion/is-prop-valid'
import { QueryClientProvider } from '@tanstack/react-query'
import {
  RouterProvider as TanstackRouterProvider,
  createRouter,
} from '@tanstack/react-router'
import { LazyMotion, domAnimation } from 'motion/react'
import posthog from 'posthog-js'
import { PostHogProvider } from 'posthog-js/react'
import { PropsWithChildren, StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { StyleSheetManager } from 'styled-components'
import { TRPCProvider, queryClient, trpcClient, trpcProxy } from '~/services/trpc'

// styled-components v6: Restore v5 behavior for shouldForwardProp
// This prevents custom props from being passed to DOM elements
function shouldForwardProp(propName: string, target: unknown) {
  if (typeof target === 'string') {
    // For HTML elements, forward the prop if it is a valid HTML attribute
    return isPropValid(propName)
  }
  // For other elements, forward all props
  return true
}

import { getConfigValue } from './helpers/envHelpers'
import './main.css'
import { routeTree } from './routeTree.gen'

const container = document.getElementById('root')
if (!container) throw new Error('missing root container')
const root = createRoot(container)

const isDevelopmentEnv = getConfigValue('VITE_VERSION') === 'develop'
const router = createRouter({
  scrollRestoration: true,
  routeTree,
  defaultPreload: 'viewport',
  context: {
    trpc: trpcProxy,
    queryClient,
  },
  Wrap: function WrapComponent({ children }: PropsWithChildren) {
    return (
      <QueryClientProvider client={queryClient}>
        <TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
          {children}
        </TRPCProvider>
      </QueryClientProvider>
    )
  },
})

// Register things for typesafety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

if (!isDevelopmentEnv) {
  posthog.init(import.meta.env.VITE_PUBLIC_POSTHOG_KEY, {
    api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
    enable_recording_console_log: true,
  })
}

root.render(
  <StrictMode>
    <StyleSheetManager shouldForwardProp={shouldForwardProp}>
      <PostHogProvider client={posthog}>
        <LazyMotion features={domAnimation}>
          <TanstackRouterProvider router={router} />
        </LazyMotion>
      </PostHogProvider>
    </StyleSheetManager>
  </StrictMode>,
)
